import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bell, Eye, Trash2, MessageCircle, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface AdminNotification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  recipient_type: string;
  recipient_id?: string;
  is_sent: boolean;
  created_at: string;
}

interface AdminNotificationsProps {
  userType: 'landlord' | 'tenant';
  userId: string;
}

const AdminNotifications = ({ userType, userId }: AdminNotificationsProps) => {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    
    // Subscribe to real-time updates for all notification types
    const channel = supabase
      .channel('admin-notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_notifications'
        },
        (payload) => {
          const newNotification = payload.new as AdminNotification;
          console.log('إشعار جديد:', newNotification);
          
          // تحقق من إذا كان الإشعار موجه للمستخدم الحالي
          const isForCurrentUser = 
            newNotification.recipient_type === 'all' ||
            (newNotification.recipient_type === userType && newNotification.recipient_id === userId) ||
            (newNotification.recipient_type === 'individual' && newNotification.recipient_id === userId);
          
          if (isForCurrentUser) {
            setNotifications(prev => [newNotification, ...prev]);
            
            // Show toast notification
            toast({
              title: newNotification.title,
              description: newNotification.message,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userType, userId]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('admin_notifications')
        .select('*')
        .or(`recipient_type.eq.all,and(recipient_type.eq.${userType},recipient_id.eq.${userId}),and(recipient_type.eq.individual,recipient_id.eq.${userId})`)
        .order('created_at', { ascending: false });

      if (error) {
        return;
      }
      
      setNotifications(data || []);
    } catch (error) {
      // Silent error handling
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      const { error } = await supabase
        .from('admin_notifications')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      // إزالة الإشعار من القائمة المحلية فوراً
      setNotifications(prev => prev.filter(notification => notification.id !== id));
      
      toast({
        title: "تم الحذف",
        description: "تم حذف الإشعار بنجاح"
      });
    } catch (error) {
      console.error('خطأ في حذف الإشعار:', error);
      toast({
        title: "خطأ في الحذف",
        description: "حدث خطأ أثناء حذف الإشعار",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-right flex items-center gap-2">
          <Bell className="h-5 w-5 text-blue-600" />
          إشعارات المشرف ({notifications.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>لا توجد إشعارات من المشرف</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {notifications.map(notification => (
              <div
                key={notification.id}
                className="p-4 border rounded-lg bg-blue-50 border-blue-200"
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 text-right">
                    <div className="flex items-center gap-2 mb-2 justify-end">
                      <Badge 
                        variant={notification.recipient_type === 'all' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {notification.recipient_type === 'all' ? 'عام' : 'شخصي'}
                      </Badge>
                      <h4 className="font-medium">{notification.title}</h4>
                    </div>
                    <p className="text-gray-700 text-sm mb-2">{notification.message}</p>
                      <div className="flex items-center gap-1 text-xs text-gray-500 justify-end">
                        <Calendar className="h-3 w-3" />
                        {new Intl.DateTimeFormat('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }).format(new Date(notification.created_at))}
                      </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteNotification(notification.id)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="حذف الإشعار"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminNotifications;